import { ApolloError, useMutation } from "@apollo/client";
import ForwardIcon from "@mui/icons-material/Forward";
import PersonIcon from "@mui/icons-material/Person";
import ReplyIcon from "@mui/icons-material/Reply";
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { SafeDialog } from "components/Dialog";
import { RejectionRoutingAction, SubmissionEncampStatus } from "generated-graphql/graphql";
import { RESOLVE_SUBMISSION_REJECTION_MUTATION } from "queries/submissions";
import { useCallback, useEffect, useState, type ChangeEvent } from "react";

import { getEncampStatusDisplay } from "./SubmissionStatusChipWithPopover";

const ASSIGNEE_ENCAMP_STATUSES = [
  SubmissionEncampStatus.NeedsCorrection,
  SubmissionEncampStatus.Resolving,
] as const;

const ASSIGNEE_ENCAMP_OPTION_DETAILS: Record<(typeof ASSIGNEE_ENCAMP_STATUSES)[number], string> = {
  [SubmissionEncampStatus.NeedsCorrection]:
    "Assignee must fix issues before resubmitting for review",
  [SubmissionEncampStatus.Resolving]: "Encamp is working to resolve the issue internally",
};

function StatusIndicator({ label, emoji }: { label: string; emoji: string }) {
  return (
    <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
      {emoji} {label}
    </Typography>
  );
}

interface RejectionRoutingDialogProps {
  open: boolean;
  onClose: () => void;
  submissionId: string;
  onSuccess?: () => void;
  /**
   * Extra query names to refetch after resolving, merged with the defaults.
   * The waste submission stepper passes `["GetSubmissionStepper"]` so its view
   * refreshes (the default `GetSubmission` only covers the legacy form).
   */
  additionalRefetchQueries?: string[];
}

export function RejectionRoutingDialog({
  open,
  onClose,
  submissionId,
  onSuccess,
  additionalRefetchQueries,
}: RejectionRoutingDialogProps) {
  const alerts = useAlerts();
  const [selectedAction, setSelectedAction] = useState<RejectionRoutingAction>(
    RejectionRoutingAction.SendToAssignee
  );
  const [assigneeEncampStatus, setAssigneeEncampStatus] = useState<SubmissionEncampStatus>(
    SubmissionEncampStatus.NeedsCorrection
  );
  const [reason, setReason] = useState("");

  const [resolveRejection, { loading }] = useMutation(RESOLVE_SUBMISSION_REJECTION_MUTATION, {
    refetchQueries: [
      "WasteSubmissionsForStaff",
      "WasteSubmissionsStaffSummary",
      "GetSubmission",
      // Retyping the rejected document changes issues, computed only by `submission`.
      "GetSubmissionIssues",
      ...(additionalRefetchQueries ?? []),
    ],
    awaitRefetchQueries: true,
  });

  useEffect(() => {
    if (open) {
      setSelectedAction(RejectionRoutingAction.SendToAssignee);
      setAssigneeEncampStatus(SubmissionEncampStatus.NeedsCorrection);
      setReason("");
    }
  }, [open]);

  const resetLocalState = useCallback(() => {
    setSelectedAction(RejectionRoutingAction.SendToAssignee);
    setAssigneeEncampStatus(SubmissionEncampStatus.NeedsCorrection);
    setReason("");
  }, []);

  const handleClose = useCallback(() => {
    if (loading) {
      return;
    }
    resetLocalState();
    onClose();
  }, [loading, onClose, resetLocalState]);

  const handleActionChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === RejectionRoutingAction.SendToAssignee) {
      setSelectedAction(RejectionRoutingAction.SendToAssignee);
      return;
    }
    if (value === RejectionRoutingAction.PushForward) {
      setSelectedAction(RejectionRoutingAction.PushForward);
      return;
    }
    if (value === RejectionRoutingAction.SendBackToReviewer) {
      setSelectedAction(RejectionRoutingAction.SendBackToReviewer);
    }
  }, []);

  const handleAssigneeEncampChange = useCallback(
    (event: SelectChangeEvent<SubmissionEncampStatus>) => {
      const value = event.target.value;
      if (
        value === SubmissionEncampStatus.NeedsCorrection ||
        value === SubmissionEncampStatus.Resolving
      ) {
        setAssigneeEncampStatus(value);
      }
    },
    []
  );

  const handleConfirm = useCallback(async () => {
    if (selectedAction === RejectionRoutingAction.PushForward && reason.trim() === "") {
      alerts.error("A reason is required when pushing forward.");
      return;
    }

    const input: {
      submissionId: string;
      action: RejectionRoutingAction;
      newEncampStatus?: SubmissionEncampStatus;
      reason?: string;
    } = {
      submissionId,
      action: selectedAction,
    };

    if (selectedAction === RejectionRoutingAction.SendToAssignee) {
      input.newEncampStatus = assigneeEncampStatus;
    }

    if (selectedAction === RejectionRoutingAction.PushForward) {
      input.reason = reason.trim();
    }

    try {
      await resolveRejection({ variables: { input } });
      alerts.success("Rejection resolved successfully.");
      resetLocalState();
      onClose();
      onSuccess?.();
    } catch (caught) {
      // Unwrap ApolloError so users see the actual server message, not the wrapper.
      if (caught instanceof ApolloError) {
        const graphQLMessage = caught.graphQLErrors.map((e) => e.message).join("; ") || undefined;
        const networkMessage = caught.networkError?.message;
        alerts.error(graphQLMessage ?? networkMessage ?? caught.message, caught);
      } else if (caught instanceof Error) {
        alerts.error(caught.message, caught);
      } else {
        alerts.error("Failed to resolve rejection.");
      }
    }
  }, [
    alerts,
    assigneeEncampStatus,
    onClose,
    onSuccess,
    reason,
    resetLocalState,
    resolveRejection,
    selectedAction,
    submissionId,
  ]);

  const confirmDisabled =
    loading || (selectedAction === RejectionRoutingAction.PushForward && reason.trim() === "");

  return (
    <SafeDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Resolve rejected submission</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Choose how to route this submission. The submission is currently in{" "}
            <StatusIndicator label="Rejected" emoji="❌" /> Encamp status
          </Typography>

          <RadioGroup value={selectedAction} onChange={handleActionChange}>
            <Stack spacing={2}>
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    cursor: loading ? "default" : "pointer",
                  }}
                  onClick={() =>
                    !loading && setSelectedAction(RejectionRoutingAction.SendToAssignee)
                  }
                >
                  <Radio
                    checked={selectedAction === RejectionRoutingAction.SendToAssignee}
                    value={RejectionRoutingAction.SendToAssignee}
                    disabled={loading}
                    size="small"
                    sx={{ p: 0 }}
                    inputProps={{ "aria-label": "Send to Assignee" }}
                  />
                  <PersonIcon fontSize="small" color="action" sx={{ mt: "2px" }} />
                  <Box>
                    <Typography variant="body2">Send to Assignee</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Clear the rejection and keep the submission{" "}
                      <StatusIndicator label="In Progress" emoji="🚧" />. The assignee will rework
                      it.
                    </Typography>
                    {selectedAction === RejectionRoutingAction.SendToAssignee && (
                      <Box sx={{ mt: 1.5, maxWidth: 320 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="rejection-routing-encamp-label">Encamp status</InputLabel>
                          <Select<SubmissionEncampStatus>
                            labelId="rejection-routing-encamp-label"
                            label="Encamp status"
                            value={assigneeEncampStatus}
                            onChange={handleAssigneeEncampChange}
                            disabled={loading}
                            renderValue={(v) => getEncampStatusDisplay(v)}
                          >
                            {ASSIGNEE_ENCAMP_STATUSES.map((status) => (
                              <MenuItem key={status} value={status}>
                                <ListItemText
                                  primary={getEncampStatusDisplay(status)}
                                  secondary={ASSIGNEE_ENCAMP_OPTION_DETAILS[status]}
                                />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>

              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    cursor: loading ? "default" : "pointer",
                  }}
                  onClick={() => !loading && setSelectedAction(RejectionRoutingAction.PushForward)}
                >
                  <Radio
                    checked={selectedAction === RejectionRoutingAction.PushForward}
                    value={RejectionRoutingAction.PushForward}
                    disabled={loading}
                    size="small"
                    sx={{ p: 0 }}
                    inputProps={{ "aria-label": "Push Forward" }}
                  />
                  <ForwardIcon fontSize="small" color="action" sx={{ mt: "2px" }} />
                  <Box>
                    <Typography variant="body2">Push Forward</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Override the rejection and move directly to{" "}
                      <StatusIndicator label="Awaiting Submission" emoji="✅" />. The
                      reviewer&apos;s rejected status is preserved for audit.
                    </Typography>
                    {selectedAction === RejectionRoutingAction.PushForward && (
                      <Box sx={{ mt: 1.5 }}>
                        <TextField
                          label="Reason"
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          fullWidth
                          required
                          multiline
                          minRows={2}
                          disabled={loading}
                          helperText="Required for audit when overriding a rejection."
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  cursor: loading ? "default" : "pointer",
                }}
                onClick={() =>
                  !loading && setSelectedAction(RejectionRoutingAction.SendBackToReviewer)
                }
              >
                <Radio
                  checked={selectedAction === RejectionRoutingAction.SendBackToReviewer}
                  value={RejectionRoutingAction.SendBackToReviewer}
                  disabled={loading}
                  size="small"
                  sx={{ p: 0 }}
                  inputProps={{ "aria-label": "Send Back to Reviewer" }}
                />
                <ReplyIcon fontSize="small" color="action" sx={{ mt: "2px" }} />
                <Box>
                  <Typography variant="body2">Send Back to Reviewer</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Reset the reviewer&apos;s progress and re-initiate the review process.
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </RadioGroup>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleConfirm()}
          disabled={confirmDisabled}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {loading ? "Confirming…" : "Confirm"}
        </Button>
      </DialogActions>
    </SafeDialog>
  );
}
